import { Button } from "@/components/ui/button";
import { Heart, Leaf } from "lucide-react";

export default function Hero() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-20 md:pb-32 overflow-hidden"
      style={{ background: "var(--bg-primary)" }}
    >
      {/* soft decorative blobs */}
      <div
        aria-hidden
        className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full opacity-60 blur-3xl"
        style={{ background: "var(--bg-secondary)" }}
      />
      <div
        aria-hidden
        className="absolute top-1/2 -right-32 w-[360px] h-[360px] rounded-full opacity-50 blur-3xl"
        style={{ background: "var(--gold-light)" }}
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <p className="section-eyebrow fade-up" data-testid="hero-eyebrow">
            <Leaf size={12} className="inline mr-2 -mt-0.5" />
            Postpartum Doula Care · Greater Brisbane
          </p>
          <h1
            className="font-serif-display fade-up delay-1 mt-4 text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight"
            style={{ color: "var(--text-main)" }}
            data-testid="hero-headline"
          >
            Nurturing the mother,
            <br />
            <em
              className="not-italic"
              style={{ color: "var(--blush)", fontFamily: "Cormorant Garamond, serif", fontStyle: "italic" }}
            >
              welcoming
            </em>{" "}
            the baby.
          </h1>
          <p
            className="fade-up delay-2 mt-7 max-w-xl text-base md:text-lg leading-relaxed"
            style={{ color: "var(--text-muted)" }}
            data-testid="hero-sub"
          >
            Tender, expert postpartum support — overnight care, lactation, meal nourishment, and gentle
            guidance for those first sacred weeks. So you can rest, heal, and bond.
          </p>

          <div className="fade-up delay-3 mt-10 flex flex-col sm:flex-row gap-4">
            <Button
              data-testid="hero-book-btn"
              onClick={() => scrollTo("booking")}
              className="rounded-full h-12 px-8 text-sm tracking-wide transition-transform hover:scale-105"
              style={{ background: "var(--blush)", color: "#FAF9F6" }}
            >
              <Heart size={16} className="mr-2" />
              Book a Free Consultation
            </Button>
            <Button
              data-testid="hero-services-btn"
              onClick={() => scrollTo("services")}
              variant="outline"
              className="rounded-full h-12 px-8 text-sm tracking-wide border-2 hover:bg-transparent transition-transform hover:scale-105"
              style={{ borderColor: "var(--gold)", color: "var(--text-main)", background: "transparent" }}
            >
              Explore Services
            </Button>
          </div>

          <div className="fade-up delay-3 mt-12 flex flex-wrap items-center gap-x-8 gap-y-3">
            {[
              "In-home care",
              "Greater Brisbane",
              "Non-judgmental support",
              "Free first consultation",
            ].map((tag) => (
              <div key={tag} className="flex items-center gap-2 text-xs uppercase tracking-[0.22em]" style={{ color: "var(--text-muted)" }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: "var(--gold)" }} />
                {tag}
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="hero-arch brand-soft-shadow floaty" data-testid="hero-image-wrap">
            <img
              src="https://images.unsplash.com/photo-1549229048-442fb5a64224?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxtb3RoZXIlMjBuZXdib3JuJTIwYmFieSUyMGhhbmRzfGVufDB8fHx8MTc3ODI4OTUyNXww&ixlib=rb-4.1.0&q=85"
              alt="Mother holding newborn"
              className="w-full h-[520px] object-cover"
              loading="eager"
            />
          </div>
          <div
            className="absolute -bottom-6 -left-6 px-6 py-4 rounded-2xl brand-soft-shadow hidden md:flex items-center gap-3"
            style={{ background: "var(--bg-tertiary)", border: "1px solid var(--border-soft)" }}
            data-testid="hero-floating-card"
          >
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: "var(--bg-secondary)" }}
            >
              <Heart size={18} color="var(--blush)" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest" style={{ color: "var(--gold)" }}>
                In-home support
              </div>
              <div className="text-sm font-medium" style={{ color: "var(--text-main)" }}>
                Across Greater Brisbane
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
